import styles from "./VisitsPerShiftChip.module.scss";
import { t } from "@lingui/core/macro";
import { Plural } from "@lingui/react/macro";
import Chip from "@medoma/components/Chip/Chip";
import CustomProperties from "@medoma/components/CustomProperties/CustomProperties";
import { Text } from "@medoma/components/Text/Text";
import { Popover } from "@/components/Popover/Popover";

type VisitsPerShiftChipProps = {
  visitsPerShift: number | null;
} & (
  | {
      shape: "pill";
      popover?: { enabled: boolean; content: React.ReactNode };
    }
  | { shape?: "default" }
);

// Thresholds based on a regular visit load for several sites
// These numbers will probably need to be adjusted over time
const HIGH_VISITS_PER_SHIFT_THRESHOLD = 6;
const MEDIUM_VISITS_PER_SHIFT_THRESHOLD = 4;

const visitsPerShiftChipClass = (visitsPerShift: number) => {
  if (visitsPerShift >= HIGH_VISITS_PER_SHIFT_THRESHOLD) {
    return styles.highVisitsPerShift;
  }
  if (visitsPerShift >= MEDIUM_VISITS_PER_SHIFT_THRESHOLD) {
    return styles.mediumVisitsPerShift;
  }
  return styles.lowVisitsPerShift;
};

const formatVisitsPerShift = (visitsPerShift: number) =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
  }).format(visitsPerShift);

const NoShifts = ({ label }: { label: string }) => (
  <div className={styles.noShifts}>
    <Chip size="small">{label}</Chip>
  </div>
);

const VisitsPerShiftContent = ({
  size,
  visitsPerShift,
}: {
  size: "small" | "medium";
  visitsPerShift: number;
}) => (
  <>
    <CustomProperties className={visitsPerShiftChipClass(visitsPerShift)}>
      <Chip size={size} weight="medium">
        {formatVisitsPerShift(visitsPerShift)}
      </Chip>
    </CustomProperties>
    <Text element="span" size="small" color="faded">
      <Plural value={visitsPerShift} one="besök" other="besök" />/{t`pass`}
    </Text>
  </>
);

const VisitsPerShiftPill = ({ visitsPerShift }: { visitsPerShift: number }) => (
  <div className={styles.visitsPerShiftPill}>
    <VisitsPerShiftContent size="small" visitsPerShift={visitsPerShift} />
  </div>
);

export const VisitsPerShiftChip = (props: VisitsPerShiftChipProps) => {
  const { visitsPerShift, shape } = props;

  if (shape === "pill") {
    const chip =
      visitsPerShift === null ? (
        <NoShifts label={t`Inga arbetspass`} />
      ) : (
        <VisitsPerShiftPill visitsPerShift={visitsPerShift} />
      );

    if (props.popover?.enabled === true) {
      return (
        <div className={styles.popover}>
          <Popover trigger={{ custom: chip }}>{props.popover.content}</Popover>
        </div>
      );
    }

    return chip;
  }

  return (
    <div className={styles.visitsPerShift}>
      {visitsPerShift !== null ? (
        <VisitsPerShiftContent size="medium" visitsPerShift={visitsPerShift} />
      ) : (
        <NoShifts label={t`0 pass`} />
      )}
    </div>
  );
};
